import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Search, Plus, X, Check, ChevronDown, Loader2 } from "lucide-react";


/**
 * Multi-select con búsqueda + botón "+" para agregar opciones custom.
 * - Carga el catálogo desde /api/contribuyentes/actividades-especificas/catalogo
 * - Almacena/devuelve un List[str] de nombres seleccionados (no IDs).
 * - El "+" llama POST /api/contribuyentes/actividades-especificas (idempotente).
 */
const ActividadesEspecificasSelector = ({
  value = [],
  onChange,
  placeholder = "Seleccione actividades específicas...",
  disabled = false,
  testid = "actividades-especificas-selector",
}) => {
  const { API, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const searchRef = useRef(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchCatalog = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/contribuyentes/actividades-especificas/catalogo`, { headers });
      setItems(r.data.items || []);
    } catch {
      toast.error("No se pudo cargar el catálogo de actividades");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCatalog(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (open && searchRef.current) setTimeout(() => searchRef.current?.focus(), 50);
  }, [open]);

  const selected = value || [];
  const selectedSet = useMemo(() => new Set(selected.map((s) => s.toLowerCase())), [selected]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.nombre.toLowerCase().includes(q));
  }, [items, search]);

  const exactMatch = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    return items.find((it) => it.nombre.toLowerCase() === q) || null;
  }, [items, search]);

  const canCreate = search.trim().length > 0 && !exactMatch && !selectedSet.has(search.trim().toLowerCase());

  const toggleItem = (nombre) => {
    if (selectedSet.has(nombre.toLowerCase())) {
      onChange(selected.filter((s) => s.toLowerCase() !== nombre.toLowerCase()));
    } else {
      onChange([...selected, nombre]);
    }
  };

  const removeItem = (nombre) => {
    onChange(selected.filter((s) => s !== nombre));
  };

  const createCustom = async () => {
    const nombre = search.trim();
    if (!nombre) return;
    setCreating(true);
    try {
      const r = await axios.post(
        `${API}/contribuyentes/actividades-especificas`,
        { nombre },
        { headers },
      );
      const item = r.data.item;
      // Actualizar catálogo local
      setItems((prev) => {
        const exists = prev.find((i) => i.id === item.id);
        return exists ? prev : [...prev, item].sort((a, b) => a.nombre.localeCompare(b.nombre));
      });
      // Auto-seleccionar
      if (!selectedSet.has(item.nombre.toLowerCase())) {
        onChange([...selected, item.nombre]);
      }
      setSearch("");
      toast.success(r.data.created ? `"${item.nombre}" agregada al catálogo` : `"${item.nombre}" ya existía, seleccionada`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo agregar la actividad");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="w-full" data-testid={testid}>
      <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={`w-full min-h-[40px] flex items-center justify-between gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm
              ${disabled ? "opacity-60 cursor-not-allowed" : "hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"}`}
            data-testid={`${testid}-trigger`}
          >
            <div className="flex flex-wrap gap-1 flex-1 min-w-0">
              {selected.length === 0 ? (
                <span className="text-slate-400">{placeholder}</span>
              ) : (
                selected.map((s) => (
                  <span
                    key={s}
                    className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5 text-xs font-medium"
                    data-testid={`${testid}-chip-${s}`}
                  >
                    {s}
                    {!disabled && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeItem(s); }}
                        className="hover:bg-blue-200 rounded-full p-0.5"
                        aria-label={`Quitar ${s}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </span>
                ))
              )}
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[480px] p-0" align="start">
          <div className="border-b p-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
              <Input
                ref={searchRef}
                className="pl-8 h-9"
                placeholder="Buscar o escribir para agregar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canCreate) {
                    e.preventDefault();
                    createCustom();
                  }
                }}
                data-testid={`${testid}-search`}
              />
            </div>
          </div>

          <div className="max-h-[280px] overflow-y-auto py-1">
            {loading ? (
              <div className="flex items-center justify-center py-8 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando catálogo...
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="px-3 py-6 text-center text-slate-400 text-sm">
                No hay coincidencias. {canCreate && "Usa el botón + abajo para agregar."}
              </div>
            ) : (
              filteredItems.map((it) => {
                const isChecked = selectedSet.has(it.nombre.toLowerCase());
                return (
                  <button
                    type="button"
                    key={it.id}
                    onClick={() => toggleItem(it.nombre)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-left hover:bg-slate-50
                      ${isChecked ? "bg-blue-50/50" : ""}`}
                    data-testid={`${testid}-option-${it.nombre}`}
                  >
                    <span className="flex items-center gap-2">
                      <span className={`w-4 h-4 border rounded flex-shrink-0 flex items-center justify-center
                        ${isChecked ? "bg-blue-600 border-blue-600" : "border-slate-300"}`}>
                        {isChecked && <Check className="w-3 h-3 text-white" />}
                      </span>
                      <span className="text-slate-700">{it.nombre}</span>
                    </span>
                    {it.origen === "custom" && (
                      <span className="text-[9px] uppercase text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">custom</span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {canCreate && (
            <div className="border-t p-2">
              <Button
                type="button"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-sm"
                onClick={createCustom}
                disabled={creating}
                data-testid={`${testid}-btn-create`}
              >
                {creating ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-2" />}
                Agregar "{search.trim()}"
              </Button>
            </div>
          )}

          {selected.length > 0 && (
            <div className="border-t p-2 flex items-center justify-between text-xs text-slate-500 bg-slate-50">
              <span>{selected.length} seleccionada(s)</span>
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-red-600 hover:underline"
                data-testid={`${testid}-btn-clear`}
              >
                Limpiar todas
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ActividadesEspecificasSelector;
